import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import {
  useMachineDisplay,
  formatXtRange,
  formatUsdtRangeFromXt,
} from '../../hooks/useMachineDisplay';
import { formatUSDT } from '../../utils/format';
import { parseError } from '../../utils/ethers';
import {
  TIER_LABELS,
  MACHINE_IMAGES,
  TIER_TAGLINES,
  TIER_TIER_LEVEL,
} from '../../utils/constants';
import { Spinner } from '../common/Loader';
import '../common/Modal.css';

export default function MachineDetailModal({
  tierIndex,
  tier,
  balances,
  xtPrice,
  onClose,
  onDone,
  isRegistered,
  actionFn,
  actionLabel,
  actionDisabled = false,
  successMsg,
  requireRegistered = true,
}) {
  const { write } = useContracts();
  const notify = useNotify();
  const { config } = useMachineDisplay();
  const [imgError, setImgError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setImgError(false);
    setBusy(false);
    // NOTE: intentionally do NOT lock body scroll — the modal itself scrolls
    // (see .modal-content overflow-y). Locking body scroll caused the Purchase
    // button to be hidden on smaller viewports.
  }, [tierIndex]);

  if (tier === null || tier === undefined) return null;

  const name = TIER_LABELS[tierIndex] || `Tier ${tierIndex + 1}`;
  const image = MACHINE_IMAGES[tierIndex];
  const rank = TIER_TIER_LEVEL[tierIndex] || 'NORMAL';
  const tagline = TIER_TAGLINES[tierIndex] || '';
  const xtRange = formatXtRange(tierIndex, config);
  const usdtRange = formatUsdtRangeFromXt(tierIndex, config, xtPrice);
  const label = actionLabel || 'Purchase Machine';

  async function handleAction() {
    if (!write) return notify.error('Please connect your wallet on the correct network.');
    if (requireRegistered && !isRegistered) return notify.error('Please register first.');
    if (balances.usdt.lt(tier.priceUSDT)) {
      return notify.error(`Insufficient USDT. Need ${formatUSDT(tier.priceUSDT)} USDT.`);
    }

    setBusy(true);
    try {
      // Approve if needed — target is the Xterra contract itself
      if (balances.allowance.lt(tier.priceUSDT)) {
        notify.info('Approving USDT...');
        const approveTx = await write.usdt.approve(write.logic.address, ethers.constants.MaxUint256);
        await approveTx.wait();
        notify.success('USDT approved.');
      }

      notify.info(`Processing ${name}...`);
      const tx = actionFn
        ? await actionFn(tierIndex)
        : await write.logic.invest(tierIndex);
      await tx.wait();
      notify.success(successMsg || `${name} activated!`);
      if (onDone) onDone();
      onClose();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content machine-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mm-close" onClick={onClose} aria-label="Close">×</button>

        <div className="mm-image-area">
          {imgError || !image ? (
            <div className="mm-fallback">
              <div className="mm-fallback-hex" />
              <div className="mm-fallback-icon">⛏</div>
            </div>
          ) : (
            <img
              className="mm-image"
              src={image}
              alt={name}
              onError={() => setImgError(true)}
            />
          )}
        </div>

        <div className="mm-body">
          <div className="mm-header">
            <div className="mm-title">
              <span
                className={`machine-tier-badge tier-${rank}`}
                style={{ position: 'static', display: 'inline-block', marginBottom: 8 }}
              >
                {rank}
              </span>
              <h2>{name}</h2>
              <div className="tagline">{tagline}</div>
            </div>
            <div className="mm-price-tag">
              <div className="p-label">Price</div>
              <div className="p-value">
                {formatUSDT(tier.priceUSDT, 0)}
                <span className="cur">USDT</span>
              </div>
            </div>
          </div>

          <div className="mm-specs mm-specs-2">
            <div className="mm-spec">
              <div className="s-label">Daily Mining</div>
              <div className="s-value">
                {xtRange}
                <span className="cur">XT / day</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
                ≈ {usdtRange} USDT / day
              </div>
            </div>
            <div className="mm-spec">
              <div className="s-label">Runtime</div>
              <div className="s-value">
                {Number(tier.miningDays)}
                <span className="cur">days</span>
              </div>
            </div>
          </div>

          <button
            className="mm-purchase"
            onClick={handleAction}
            disabled={busy || actionDisabled}
          >
            {busy ? <><Spinner /> Processing...</> : <>⛏ {label}</>}
          </button>

          <div className="mm-hint">
            Your USDT balance: <strong style={{ color: 'var(--accent)' }}>{formatUSDT(balances.usdt)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
